import { z } from 'zod';
// [12.2L-150] M1 — marketing assets (creative pool) shared schemas.
//
// Distinct from `asset.ts` (brand identity assets — logos, type,
// identity photos). Marketing assets are campaign creative inputs:
// product shots, lifestyle imagery, event footage, ugc. The
// strategist (M3) draws from this pool when assembling Meta proposals.
export const marketingAssetMediaTypeSchema = z.enum(['image', 'video']);
// [12.2L-163] M13.1 — kind discriminator. raw = founder-uploaded
// source material (mag composes from). rendered = mag-generated PNGs
// via the M13 bridge. Meta channel creatives[].assetUrl points at
// kind='rendered' rows only.
export const marketingAssetKindSchema = z.enum(['raw', 'rendered']);
export const marketingAssetFormatSchema = z.enum([
    'square', // 1:1 — feed default
    'portrait', // 4:5 — feed-optimized
    'landscape', // 16:9 / 1.91:1 — link share
    'story', // 9:16 — stories + reels
    'unknown', // pre-classification fallback
]);
export const marketingAssetUploadSourceSchema = z.enum([
    'dashboard', // creative-pool page upload
    'slack', // shared in mag's slack channel
    'telegram', // shared in mag's telegram dm
    'mag', // mag-generated (M13)
]);
/** Mag's vision-classifier output, persisted on the row. Matches the
 *  brand-image-classify agent's output shape so the same agent can
 *  run against both bags. Each field is optional so partial runs
 *  (classification failures, slow re-runs) don't block the insert. */
export const marketingAssetVisionClassificationSchema = z.object({
    subject: z.string().max(40).optional(),
    style: z.string().max(40).optional(),
    mood: z.string().max(120).nullable().optional(),
    dominantColors: z.array(z.string().max(32)).max(5).optional(),
    quality: z.enum(['usable', 'reject']).optional(),
    rejectReason: z.string().max(200).nullable().optional(),
});
/** DTO shape sent to the dashboard. Mirrors the row but strips
 *  ids the founder doesn't need (organization_id) and stamps URLs
 *  as their CDN form. */
export const marketingAssetDtoSchema = z.object({
    id: z.string().uuid(),
    brandId: z.string().uuid(),
    kind: marketingAssetKindSchema,
    /** When kind='rendered', the creative_assets row this PNG was
     *  rendered from. Null on kind='raw'. */
    sourceCreativeAssetId: z.string().uuid().nullable(),
    /** [12.2L-165] When this rendered row's source is an organic IG
     *  post (promote_post Meta channel kind), the IG media id. Null
     *  on raw uploads + fresh bridge renders. */
    sourceIgMediaId: z.string().nullable(),
    assetUrl: z.string().url(),
    thumbnailUrl: z.string().url().nullable(),
    mediaType: marketingAssetMediaTypeSchema,
    format: marketingAssetFormatSchema,
    durationMs: z.number().int().min(0).nullable(),
    filename: z.string(),
    mimeType: z.string(),
    bytes: z.number().int().min(0),
    occasion: z.string().max(120).nullable(),
    productId: z.string().nullable(),
    tags: z.array(z.string()),
    visionClassification: marketingAssetVisionClassificationSchema.nullable(),
    uploadSource: marketingAssetUploadSourceSchema,
    uploadedByUserId: z.string().uuid().nullable(),
    uploadedAt: z.string(),
    archivedAt: z.string().nullable(),
    expiresAt: z.string().nullable(),
});
// ─── Mutation payloads (founder-driven edits) ────────────────────────
/** PATCH /api/marketing-assets/:id — partial update payload. The
 *  founder can correct any of: occasion, productId, tags, expiresAt.
 *  Vision classification is mag's domain, never set by the founder. */
export const updateMarketingAssetSchema = z.object({
    occasion: z.string().max(120).nullable().optional(),
    productId: z.string().max(120).nullable().optional(),
    tags: z.array(z.string().min(1).max(40)).max(20).optional(),
    expiresAt: z.string().datetime().nullable().optional(),
});
// ─── Bag classifier output (chat ingest) ─────────────────────────────
/** Output of the bag classifier mag runs on inbound chat media. Routes
 *  the asset to brand_assets (identity) or marketing_assets (creative
 *  pool) without asking the founder. Ambiguous results trigger a
 *  one-tap confirmation in chat. */
export const assetBagDecisionSchema = z.object({
    bag: z.enum(['identity', 'marketing', 'ambiguous']),
    confidence: z.enum(['high', 'medium', 'low']),
    reasoning: z.string().max(280),
    /** When bag === 'marketing', the agent's best guess for the asset's
     *  occasion ("spring launch", "product hero"). Null when nothing
     *  clear. The marketing_assets row inserts with this as a starting
     *  occasion the founder can correct. */
    occasion: z.string().max(120).nullable(),
    /** Initial tag suggestions for the marketing bag. Empty array when
     *  bag === 'identity'. */
    marketingTags: z.array(z.string().min(1).max(40)).max(5),
});
//# sourceMappingURL=marketing-asset.js.map